import { DataContainer } from '../components/DataContainer';
import BrandFilter from '../components/BrandFilter';
import { useSelector } from 'react-redux';
import { getBrand } from '../brands';
import ArchiveTable from '../tables/ArchiveTable';
import downloadCsv from 'download-csv';
import * as common from '../tables/common';
import Section from '../components/Section';
import RejectedTable, { REJECTED_NAMES } from '../tables/RejectedTable';
import FNS from '../api';
import { ARCHIVE_PAGE_SIZE } from '../store/shownDataSlice';


const escapeAndQuote = value => {
    const str = String(value ?? "");
    return `"${str.replace(/"/g, '""')}"`;
};

// The tables are paginated, so the Redux store only holds the current page.
// Exports must pull the full dataset from the backend (all = true)
const fetchAllArchive = async (brand) => (await FNS.getArchive(ARCHIVE_PAGE_SIZE, 0, '', '', '', true, brand)).archive;
const fetchAllRejected = async (brand) => (await FNS.getRejected(ARCHIVE_PAGE_SIZE, 0, '', '', '', true, brand)).rejected;

function Archive() {
    const brandFilter = useSelector((state) => state.shownData.brandFilter);

    async function onExportArchive() {
        const archiveData = await fetchAllArchive(brandFilter);
        const HEADERS = [
            "Tüüp",
            "Reisi kuupäev",
            "Päringu kuupäev",
            "Firma",
            "Bränd",
            "Sihtkohad",
            "Peatumisi",
            "Vahemaa",
            "Maksumus",
            "Juht",
            "Reisi nr",
            "Reisijad",
        ];

        downloadCsv([HEADERS, ...archiveData.map(row => [
            escapeAndQuote(common.displayType(row)),
            escapeAndQuote(common.displayDepart(row)),
            escapeAndQuote(common.prettifyTimestamp(row["query_created"])),
            escapeAndQuote(row["query_company"] || "-"),
            escapeAndQuote(getBrand(row["query_brand"]).name),
            escapeAndQuote(common.displayPrettyRoute(row)),
            escapeAndQuote(common.displayRouteStops(row)),
            escapeAndQuote(row["query_distance"]),
            escapeAndQuote(row["query_response_price"] || '-'),
            escapeAndQuote(common.displayDriver(row)),
            escapeAndQuote(row["type"] == "job" ? row["job_sequential_id"] : '-'),
            escapeAndQuote(row["query_passengers"]),
        ])], HEADERS, "eksport-arhiiv-" + (new Date()).toISOString() + ".csv");
    }

    async function onExportRejected() {
        const rejectedData = await fetchAllRejected(brandFilter);
        const HEADERS = [
            "Reisi kuupäev",
            "Päringu kuupäev",
            "Firma",
            "Bränd",
            "Sihtkohad",
            "Peatumisi",
            "Vahemaa",
            "Maksumus",
            "Reisijad",
            "Põhjus",
        ];
        downloadCsv([HEADERS, ...rejectedData.map(row => [
            escapeAndQuote(common.displayDepart(row)),
            escapeAndQuote(common.prettifyTimestamp(row["query_created"])),
            escapeAndQuote(row["query_company"] || "-"),
            escapeAndQuote(getBrand(row["query_brand"]).name),
            escapeAndQuote(common.displayPrettyRoute(row)),
            escapeAndQuote(common.displayRouteStops(row)),
            escapeAndQuote(row["query_distance"]),
            escapeAndQuote(row["query_response_price"] || '-'),
            escapeAndQuote(row["query_passengers"]),
            escapeAndQuote(REJECTED_NAMES[row["query_response_reject_reason_reason"]]),
        ])], HEADERS, "eksport-loobutud-" + (new Date()).toISOString() + ".csv");
    }

    async function onExportAll() {
        const [archiveData, rejectedData] = await Promise.all([fetchAllArchive(brandFilter), fetchAllRejected(brandFilter)]);
        const ARCHIVE_HEADERS = [
            "Tüüp",
            "Reisi kuupäev",
            "Päringu kuupäev",
            "Firma",
            "Bränd",
            "Sihtkohad",
            "Peatumisi",
            "Vahemaa",
            "Maksumus",
            "Juht",
            "Reisi nr",
            "Reisijad",
            "Tagasilükatud",
        ];

        // Map archive data
        const archiveRows = archiveData.map(row => ({
            type: common.displayType(row),
            depart: common.displayDepart(row),
            created: common.prettifyTimestamp(row["query_created"]),
            company: row["query_company"] || "-",
            brand: getBrand(row["query_brand"]).name,
            route: common.displayPrettyRoute(row),
            stops: common.displayRouteStops(row),
            distance: row["query_distance"],
            price: row["query_response_price"] || '-',
            driver: common.displayDriver(row),
            job_id: row["type"] == "job" ? row["job_sequential_id"] : '-',
            passengers: row["query_passengers"],
            reason: "-", // No reason for archive
        }));

        // Map rejected data
        const rejectedRows = rejectedData.map(row => ({
            type: "Tagasi lükatud",
            depart: common.displayDepart(row),
            created: common.prettifyTimestamp(row["query_created"]),
            company: row["query_company"] || "-",
            brand: getBrand(row["query_brand"]).name,
            route: common.displayPrettyRoute(row),
            stops: common.displayRouteStops(row),
            distance: row["query_distance"],
            price: row["query_response_price"] || '-',
            driver: "-", // No driver for rejected
            job_id: "-", // No job id for rejected
            passengers: row["query_passengers"],
            reason: REJECTED_NAMES[row["query_response_reject_reason_reason"]],
        }));

        // Combine so archived jobs come before rejected
        const allRows = [...archiveRows, ...rejectedRows];

        const csvRows = allRows.map(row => [
            escapeAndQuote(row.type),
            escapeAndQuote(row.depart),
            escapeAndQuote(row.created),
            escapeAndQuote(row.company),
            escapeAndQuote(row.brand),
            escapeAndQuote(row.route),
            escapeAndQuote(row.stops),
            escapeAndQuote(row.distance),
            escapeAndQuote(row.price),
            escapeAndQuote(row.driver),
            escapeAndQuote(row.job_id),
            escapeAndQuote(row.passengers),
            escapeAndQuote(row.reason)
        ]);

        downloadCsv([ARCHIVE_HEADERS, ...csvRows], ARCHIVE_HEADERS, "eksport-kõik-" + (new Date()).toISOString() + ".csv");
    }


    return (
        <div className="column g32 p32">
            <div className="row g16 center-cross">
                <button onClick={onExportAll}>Ekspordi kõik</button>
                <BrandFilter />
            </div>

            <Section title="Arhiiv">
                <DataContainer actionType={'shownData/fetchArchive'}>
                    <div className="column g32">
                        <div className="row">
                            <button onClick={onExportArchive}>Ekspordi</button>
                        </div>
                        <ArchiveTable />
                    </div>
                </DataContainer>
            </Section>

            <Section title="Tagasi lükatud tellimused">
                <DataContainer actionType={'shownData/fetchRejected'}>
                    <div className="column g32">
                        <div className="row">
                            <button onClick={onExportRejected}>Ekspordi</button>
                        </div>
                        <RejectedTable />
                    </div>
                </DataContainer>
            </Section>
        </div>
    );
}

export default Archive;